import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  isYesterday,
  isSameWeek,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'HH:mm') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, { locale: ptBR });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'd \'de\' MMM \'de\' yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat, { locale: ptBR });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'd \'de\' LLL \'de\' y, HH:mm', { locale: ptBR });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true, locale: ptBR });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'd \'de\' MMM \'de\' yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, { locale: ptBR });
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = true) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' atrás' : '';
  const timeMappings = {
    'less than a minute ago': 'agora',
    'in less than a minute': 'há pouco',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Converts a Unix timestamp to a smart contextual time format.
 * @param {number} time - Unix timestamp.
 * @returns {string} Formatted time string based on context.
 */
export const smartTimestamp = time => {
  const unixTime = fromUnixTime(time);
  const now = new Date();

  // Check if it's today by comparing year, month and day
  const isToday = now.getFullYear() === unixTime.getFullYear() &&
    now.getMonth() === unixTime.getMonth() &&
    now.getDate() === unixTime.getDate();

  // Today: show exact time (HH:mm)
  if (isToday) {
    return format(unixTime, 'HH:mm', { locale: ptBR });
  }

  // Yesterday: show "Ontem HH:mm"
  if (isYesterday(unixTime)) {
    return `Ontem ${format(unixTime, 'HH:mm', { locale: ptBR })}`;
  }

  // This week (before yesterday): show weekday name
  // weekStartsOn: 0 means week starts on Sunday
  if (isSameWeek(unixTime, now, { weekStartsOn: 0 })) {
    return format(unixTime, 'EEEE', { locale: ptBR });
  }

  // Outside this week: show exact date (dd/mm/yyyy)
  return format(unixTime, 'dd/MM/yyyy', { locale: ptBR });
};
